import React from 'react';
import { Flex, Text } from '@amzn/storm-ui';
import styled from 'styled-components';
import {
  accountSelectionListId,
  containerWithBorderBottomMixin,
  IA2AccountDropdownSearchInput,
} from './styles';
import { useAccountDropdownSearch } from './useAccountDropdownSearch';
import { IA2AccountDropdownTypeSection } from './IA2AccountDropdownTypeSection';
import { IA2AccountDropdownInfoCard } from './IA2AccountDropdownInfoCard';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { IA2AccountDropdownFooterLinks } from './IA2AccountDropdownFooterLinks';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getAccountTypeListLabel } from './AccountTypeLocalizationKeyMapping';
import { NoAccountsMessage } from './NoAccountsMessage';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { findSelectedAccountSwitcherAccount } from './accountHelper';
import { ErrorLoadingAccountsMessage } from 'src/features/header/components/AccountDropdown/ErrorLoadingAccountsMessage';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

const SEARCH_DISPLAY_COUNT = 10;

const AccountSwitcherInfoCardContainer = styled(Flex)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.base.default};
  background-color: ${({ theme }) => theme.color.gray[50]};
`;

const AccountListContainer = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: 0;
  border-top: 1px solid ${({ theme }) => theme.color.gray[200]};
  ${containerWithBorderBottomMixin};
`;

const AccountDropdownWithSearchContainer = styled(Flex)`
  max-height: 80vh;
`;

const AllAccountsMessageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  display: flex;
  justify-content: center;
`;

type IA2AccountDropdownProps = {
  advertisingAccountsDataTaktId?: string;
};

export const IA2AccountDropdown: React.VFC<IA2AccountDropdownProps> = ({
  advertisingAccountsDataTaktId,
}) => {
  const {
    currentAccountName,
    accounts,
    currentEntityId,
    accountCounter,
    loadingStatus,
  } = useAccountDropdownContext();
  const bundle = useAccountDropdownBundle();
  const chromeConfig = useChromeConfigContext();
  const isAllAccountsPageScope =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  const isFailedLoadingAccountData = loadingStatus === 'error';

  const { onSearch, accountTypeList, onClear, searchValue } =
    useAccountDropdownSearch({
      accounts,
    });

  const isSearchInputVisible = accountCounter > SEARCH_DISPLAY_COUNT;

  const accountSearchPlaceholder = bundle.getMessage(
    'account-dropdown-placeholder',
  );

  if (isAllAccountsPageScope) {
    return (
      <AllAccountsMessageContainer>
        <Text>{bundle.getMessage('global-accounts-pill-message')}</Text>
      </AllAccountsMessageContainer>
    );
  }
  const selectedAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    accounts.map((accountGroup) => accountGroup.accounts).flat(),
  );

  return (
    <AccountDropdownWithSearchContainer direction="column">
      <AccountSwitcherInfoCardContainer direction="column">
        {!!currentAccountName && <IA2AccountDropdownInfoCard />}
        {isSearchInputVisible && (
          <IA2AccountDropdownSearchInput
            id="account-switcher-search"
            placeholder={accountSearchPlaceholder}
            onChange={onSearch}
            onClear={onClear}
            fullWidth={true}
            label={accountSearchPlaceholder}
            value={searchValue}
            {...getTaktAttributes(
              'ccx-account-switcher-search',
              chromeComponents.HEADER,
            )}
          />
        )}
      </AccountSwitcherInfoCardContainer>
      <AccountListContainer
        id={accountSelectionListId}
        {...(!!advertisingAccountsDataTaktId &&
          getTaktAttributes(
            advertisingAccountsDataTaktId,
            chromeComponents.HEADER,
          ))}
      >
        {isFailedLoadingAccountData && <ErrorLoadingAccountsMessage />}
        {!isFailedLoadingAccountData && accountTypeList.length === 0 && (
          <NoAccountsMessage />
        )}
        {!isFailedLoadingAccountData &&
          accountTypeList.length > 0 &&
          accountTypeList.map((accountTypeItem, index) => (
            <IA2AccountDropdownTypeSection
              key={accountTypeItem.label}
              accounts={accountTypeItem.accounts}
              label={getAccountTypeListLabel(
                accountTypeItem.accountType,
                bundle,
              )}
              type={accountTypeItem.accountType}
              selectedEntityId={selectedAccount?.entityId}
              isLastAccountType={index === accountTypeList.length - 1}
            />
          ))}
      </AccountListContainer>
      <IA2AccountDropdownFooterLinks />
    </AccountDropdownWithSearchContainer>
  );
};
